import { useState } from "react";
import { Users, Loader2 } from "lucide-react";
import { launchpadApi } from "@/lib/api";
import useRefreshOnFocus from "@/lib/useRefreshOnFocus";
import { initials, colorFor } from "@/lib/avatar";

// TeamCapacityWidget — Jul 2026.
//
// One-glance rollup of this-week workload across every project the viewer
// can see. Powered by canonical `duration_hours` (post-Phase-A migration) —
// each task's hours are prorated across its calendar span, then summed per
// assignee. Utilization is scheduled/capacity where capacity = working days
// this week × hours per day from global settings.
//
// Rendered above At-Risk on Launchpad so a producer sees the shape of the
// week BEFORE they start firing off nudges.

function formatHours(h) {
  if (h == null) return "—";
  return h >= 10 ? Math.round(h) : Math.round(h * 10) / 10;
}

function utilizationTone(pct) {
  // Red = overloaded; amber = tight; emerald = healthy; zinc = light.
  if (pct >= 1.05) return { bar: "bg-rose-500", pill: "text-rose-300 border-rose-500/50 bg-rose-500/10", label: "overloaded" };
  if (pct >= 0.85) return { bar: "bg-amber-500", pill: "text-amber-300 border-amber-500/50 bg-amber-500/10", label: "tight" };
  if (pct >= 0.4)  return { bar: "bg-emerald-500", pill: "text-emerald-300 border-emerald-800/60 bg-emerald-500/10", label: "healthy" };
  return { bar: "bg-zinc-600", pill: "text-zinc-400 border-zinc-800 bg-zinc-900/40", label: "light" };
}

export default function TeamCapacityWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useRefreshOnFocus(() => {
    let cancelled = false;
    launchpadApi
      .getTeamCapacity()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <article
        data-testid="team-capacity-loading"
        className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5 flex items-center gap-2 text-zinc-500 text-sm"
      >
        <Loader2 className="w-4 h-4 animate-spin" /> Loading team capacity…
      </article>
    );
  }

  if (!data) return null;

  const users = data.users || [];
  const totalHours = data.total_hours || 0;
  const avgHours = data.avg_hours || 0;
  const weekCapacity = data.week_capacity || 0;

  // Empty state: no assignees this week. Skip the widget entirely rather
  // than showing a "0 hours" tombstone — Launchpad real-estate is scarce.
  // (Spec: hide when users.length === 0. Falling total_hours to 0 with a
  // non-empty users list would be surprising, but we defer to users.length
  // per spec so a 0h entry still surfaces the person for visibility.)
  if (users.length === 0) {
    return null;
  }

  // Headline sentence composed inline so it reads naturally even when there's
  // just one person on the list.
  const headline = (
    <>
      This week:{" "}
      <span className="text-zinc-100 font-mono">{formatHours(totalHours)}h</span>{" "}
      scheduled across{" "}
      <span className="text-zinc-100">
        {users.length} {users.length === 1 ? "person" : "people"}
      </span>{" "}
      <span className="text-zinc-600">
        (avg <span className="text-zinc-300 font-mono">{formatHours(avgHours)}h</span>)
      </span>
      {users.length > 0 && users[0].scheduled_hours > 0 && (
        <>
          {" · "}
          <span className="text-zinc-100">{users[0].name?.split(" ")[0] || "?"}</span>{" "}
          is at{" "}
          <span
            className={
              users[0].utilization >= 1.05
                ? "text-rose-300 font-mono"
                : users[0].utilization >= 0.85
                ? "text-amber-300 font-mono"
                : "text-zinc-100 font-mono"
            }
          >
            {formatHours(users[0].scheduled_hours)}h
          </span>
          {weekCapacity > 0 && (
            <span className="text-zinc-600 font-mono">
              {"  "}({Math.round((users[0].utilization || 0) * 100)}%)
            </span>
          )}
        </>
      )}
      .
    </>
  );

  return (
    <article
      data-testid="team-capacity-widget"
      className="border border-zinc-800 bg-zinc-950/60 rounded-md p-6 mb-5"
      style={{ borderLeftColor: "#0ea5e9", borderLeftWidth: 3 }}
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-overline text-sky-400/80 mb-1 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Team Capacity · week of {data.week_start}
          </div>
          <h2
            data-testid="team-capacity-headline"
            className="font-heading text-xl font-light text-zinc-100 leading-tight"
          >
            {headline}
          </h2>
          {weekCapacity > 0 && (
            <p className="text-xs text-zinc-500 mt-1 font-mono">
              Weekly capacity per person: {weekCapacity}h ·{" "}
              {data.hours_per_day}h/day
            </p>
          )}
        </div>
      </div>

      <ul className="space-y-1.5" data-testid="team-capacity-list">
        {users.map((u) => {
          const pct = Math.min(1.5, u.utilization || 0);
          const tone = utilizationTone(u.utilization || 0);
          const barPct = Math.min(100, Math.round(pct * 100));
          return (
            <li
              key={u.user_id}
              data-testid={`team-capacity-row-${u.user_id}`}
              className="flex items-center gap-3 py-1.5 pl-2 pr-3 rounded-sm hover:bg-zinc-900/60 transition-colors"
            >
              <span
                className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-mono font-bold text-zinc-950 shrink-0"
                style={{ backgroundColor: colorFor(u.user_id) }}
              >
                {initials(u.name || "?")}
              </span>
              <span className="text-sm text-zinc-200 truncate w-32 shrink-0">
                {u.name || "—"}
              </span>
              {/* utilization bar */}
              <div className="flex-1 h-1.5 bg-zinc-900 rounded-full relative overflow-hidden min-w-[80px]">
                <div
                  className={`absolute inset-y-0 left-0 ${tone.bar} transition-all`}
                  style={{ width: `${barPct}%` }}
                />
                {u.utilization >= 1.0 && (
                  <div
                    className="absolute inset-y-0 border-l border-zinc-800"
                    style={{ left: `${Math.min(100, Math.round(100 / Math.max(pct, 0.001)))}%` }}
                    aria-hidden="true"
                    title="100% capacity marker"
                  />
                )}
              </div>
              <span
                className="text-[11px] font-mono text-zinc-300 shrink-0 w-14 text-right"
                title={`${u.scheduled_hours}h scheduled of ${u.capacity_hours}h capacity`}
              >
                {formatHours(u.scheduled_hours)}h
              </span>
              <span
                className={`text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded-sm border shrink-0 w-24 text-center ${tone.pill}`}
              >
                {tone.label}
              </span>
            </li>
          );
        })}
      </ul>
    </article>
  );
}
